import { Alert, Button, Descriptions, Drawer, Space, Steps, Tag, Tooltip, Typography } from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { usePermissions } from '@/shared/auth/usePermissions'
import { formatDateTime, formatDuration } from '@/shared/lib/format'
import { actorLabel } from '@/features/identity/actorName'
import { useActorDirectory } from '@/features/identity/useActorDirectory'
import {
  fetchTicket,
  ticketAcceptanceStep,
  ticketStage,
  ticketTransition,
  type Ticket,
  type TicketAcceptanceStep,
  type TicketStage,
} from './crmApi'

const FLOW: readonly TicketStage[] = ['ASSIGNED', 'RECEIVED', 'CONFIRMED', 'IN_PROGRESS', 'RESOLVED', 'CLOSED']

const STAMP: Partial<Record<TicketStage, (ticket: Ticket) => string | null | undefined>> = {
  ASSIGNED: (ticket) => ticket.assignedAt,
  RECEIVED: (ticket) => ticket.receivedAt,
  CONFIRMED: (ticket) => ticket.confirmedAt,
  IN_PROGRESS: (ticket) => ticket.startedAt,
  RESOLVED: (ticket) => ticket.resolvedAt,
  CLOSED: (ticket) => ticket.closedAt,
}

interface TicketDetailsDrawerProps {
  /** `null` — yopiq */
  ticketId: string | null
  onClose: () => void
  onChanged: () => void
}

export function TicketDetailsDrawer({ ticketId, onClose, onChanged }: TicketDetailsDrawerProps) {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { can } = usePermissions()
  const actors = useActorDirectory()
  const queryClient = useQueryClient()

  const ticket = useQuery({
    queryKey: queryKeys.crm.ticket(ticketId ?? ''),
    queryFn: () => fetchTicket(ticketId!),
    enabled: ticketId !== null,
  })

  const accept = useMutation({
    mutationFn: (step: TicketAcceptanceStep) => ticketTransition(ticketId!, step),
    onSuccess: (updated) => {
      queryClient.setQueryData(queryKeys.crm.ticket(updated.id), updated)
      onChanged()
    },
  })

  const close = () => {
    accept.reset()
    onClose()
  }

  const data = ticket.data
  const stage = data ? ticketStage(data) : null
  const step = data && can('crm.ticket.update') ? ticketAcceptanceStep(data, user?.id) : null
  const current = stage === null || stage === 'UNASSIGNED' ? 0 : FLOW.indexOf(stage)

  return (
    <Drawer
      open={ticketId !== null}
      title={data?.subject ?? t('crm.ticketDetails.title')}
      onClose={close}
      width={560}
      destroyOnHidden
      extra={
        step ? (
          <Button type="primary" loading={accept.isPending} onClick={() => accept.mutate(step)}>
            {t(`crm.ticketAction.${step}`)}
          </Button>
        ) : null
      }
    >
      {ticket.isError ? (
        <Alert type="error" showIcon message={translateError(ticket.error, t)} />
      ) : null}
      {accept.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(accept.error, t)}
        />
      ) : null}

      {data ? (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <Typography.Title level={5}>{t('crm.ticketDetails.flow')}</Typography.Title>
            <Steps
              direction="vertical"
              size="small"
              current={current}
              status={stage === 'UNASSIGNED' ? 'wait' : stage === 'CLOSED' ? 'finish' : 'process'}
              items={FLOW.map((flowStage) => {
                const stamp = STAMP[flowStage]?.(data)
                return {
                  title: t(`crm.ticketStage.${flowStage}`),
                  description: stamp ? formatDateTime(stamp) : undefined,
                }
              })}
            />
          </div>

          <Descriptions
            column={1}
            size="small"
            bordered
            items={[
              {
                key: 'status',
                label: t('subscribers.columns.status'),
                children: (
                  <Space size={4} wrap>
                    <Tag>{t(`crm.ticketStatus.${data.status}`)}</Tag>
                    {stage ? <Tag color="blue">{t(`crm.ticketStage.${stage}`)}</Tag> : null}
                  </Space>
                ),
              },
              {
                key: 'assignee',
                label: t('crm.ticket.assignee'),
                children: data.assignedTo ? (
                  actorLabel(data.assignedTo, actors, '—')
                ) : (
                  <Typography.Text type="warning">{t('crm.ticket.unassigned')}</Typography.Text>
                ),
              },
              { key: 'team', label: t('crm.ticket.team'), children: data.teamName ?? '—' },
              {
                key: 'priority',
                label: t('crm.ticket.priority'),
                children: t(`crm.ticketPriority.${data.priority}`),
              },
              { key: 'dueAt', label: t('crm.ticket.dueAt'), children: formatDateTime(data.dueAt) },
              {
                key: 'confirmedBy',
                label: t('crm.ticket.confirmedBy'),
                children: data.confirmedBy
                  ? `${actorLabel(data.confirmedBy, actors, '—')} · ${formatDateTime(data.confirmedAt)}`
                  : '—',
              },
            ]}
          />

          <div>
            <Typography.Title level={5}>{t('crm.ticketTiming.title')}</Typography.Title>
            <Descriptions column={1} size="small" bordered items={timingItems(data, t)} />
          </div>
        </Space>
      ) : null}
    </Drawer>
  )
}

function timingItems(ticket: Ticket, t: TFunction) {
  const hinted = (label: string, hint: string) => (
    <Tooltip title={t(hint)}>
      <span>{t(label)}</span>
    </Tooltip>
  )
  return [
    { key: 'createdAt', label: t('crm.ticketTiming.createdAt'), children: formatDateTime(ticket.createdAt) },
    { key: 'assignedAt', label: t('crm.ticket.assignedAt'), children: formatDateTime(ticket.assignedAt) },
    {
      key: 'notifiedAt',
      label: hinted('crm.ticketTiming.notifiedAt', 'crm.ticketTiming.notifiedHint'),
      children: formatDateTime(ticket.assigneeNotifiedAt),
    },
    { key: 'receivedAt', label: t('crm.ticketTiming.receivedAt'), children: formatDateTime(ticket.receivedAt) },
    { key: 'startedAt', label: t('crm.ticketTiming.startedAt'), children: formatDateTime(ticket.startedAt) },
    { key: 'resolvedAt', label: t('crm.ticketTiming.resolvedAt'), children: formatDateTime(ticket.resolvedAt) },
    { key: 'closedAt', label: t('crm.ticketTiming.closedAt'), children: formatDateTime(ticket.closedAt) },
    {
      key: 'acknowledgeTime',
      label: hinted('crm.ticketTiming.acknowledgeTime', 'crm.ticketTiming.acknowledgeTimeHint'),
      children: formatDuration(ticket.acknowledgeSeconds),
    },
    {
      key: 'resolutionTime',
      label: hinted('crm.ticketTiming.resolutionTime', 'crm.ticketTiming.resolutionTimeHint'),
      children: formatDuration(ticket.resolutionSeconds),
    },
  ]
}
